import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { useCallback } from 'react';

import { useAccessToken } from '../../api/useAccessToken.hook';

export interface UseAITaskOptions {
    documentId: string;
}

export interface UseAITaskState {
    status: 'submitted' | 'streaming' | 'ready' | 'error';
    messages: UIMessage[];
    lastAssistantMessage: UIMessage | null;
    submit: (systemPrompt: string, userPrompt: string) => void;
    stop: (resetState?: boolean) => void;
}

export function useAITask({
    documentId
}: UseAITaskOptions): UseAITaskState {
    const accessToken = useAccessToken();

    const { messages, sendMessage, setMessages, stop: _stop, status } = useChat({
        transport: new DefaultChatTransport({
            api: `/api/v1/documents/id/${documentId}/ai/generateCitation`,
            headers: {
                Authorization: `Bearer ${accessToken}`
            },
        }),
    });

    const submit = useCallback((systemPrompt: string, userPrompt: string) => {        
        setMessages([]);

        sendMessage({
            role: 'user',
            parts: [{ type: 'text', text: userPrompt }]
        }, {
            body: {
                system: systemPrompt
            }
        });
    }, [ sendMessage, setMessages ]);

    const stop = useCallback((resetState?: boolean) => {
        _stop();

        if (resetState) {
            setMessages([]);
        }
    }, [ _stop, setMessages ]);

    const lastAssistantMessage = messages.findLast(m => m.role === 'assistant') ?? null;
    
    return {
        status,
        messages,
        lastAssistantMessage,
        submit,
        stop
    };
}
